// Wires the generated admin client to the stored admin session.
import { createAdminClient } from "@10twenty/api/admin";
import { QueryClient } from "@tanstack/react-query";
import { ADMIN_SESSION_KEY, AdminSessionManager } from "./auth/session";
import { ApiError } from "./problem";

const baseUrl = new URL("/api/admin/v1", window.location.origin).href;
const plainFetch = (request: Request) => fetch(request);

/** Web Locks keep tabs from replaying each other's refresh tokens; `App` refuses to run without them. */
export const locksSupported = typeof navigator.locks !== "undefined";

// The operator's last input in any tab, so background polling alone never
// keeps the session alive (IDLE_LIMIT_MS).
const ACTIVITY_KEY = "tentwenty.admin.activity";
const activity = {
  get(): number | null {
    const at = Number(window.localStorage.getItem(ACTIVITY_KEY) ?? NaN);
    return Number.isFinite(at) ? at : null;
  },
  set(at: number) {
    window.localStorage.setItem(ACTIVITY_KEY, String(at));
  },
};

export const session = new AdminSessionManager({
  baseUrl,
  storage: window.localStorage,
  fetch: plainFetch,
  locks: locksSupported ? navigator.locks : undefined,
  activity,
});

// Loading the page counts as input, unless the session already went idle.
session.recordActivity();
for (const type of ["pointerdown", "keydown", "wheel"] as const) {
  window.addEventListener(type, () => session.recordActivity(), { capture: true, passive: true });
}

// Sign-in and sign-out in other tabs.
window.addEventListener("storage", (event) => {
  if (event.key === null || event.key === ADMIN_SESSION_KEY) session.notifyExternalChange();
});

/** Typed admin client; attaches and refreshes the session's access token. */
export const api = createAdminClient({ baseUrl, fetch: session.authFetch });

/** Client without credentials, for sign-in and enrolment. */
export const publicApi = createAdminClient({ baseUrl, fetch: plainFetch });

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A refused credential or role is final; retrying only repeats it.
      retry: (failures, error) => !(error instanceof ApiError && error.status < 500) && failures < 2,
      refetchOnWindowFocus: true,
    },
  },
});

// Nothing one staff member loaded stays cached for the next.
let signedInAs = session.current()?.staff.user_id ?? null;
session.subscribe(() => {
  const now = session.current()?.staff.user_id ?? null;
  if (now !== signedInAs) queryClient.clear();
  signedInAs = now;
});
